import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getMuhuratRequest } from "../../redux/MuhuratCalender/muhuratSlice";
import { useAppTheme } from "../../theme/useAppTheme";

export default function MuhuratCalendar() {
  const dispatch = useDispatch();

  const { muhuratData, loading, error } = useSelector(
    (state) => state.muhurat || {},
  );
  const themeColors = useAppTheme();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState("");
  const cardRefs = useRef({});

  useEffect(() => {
    dispatch(getMuhuratRequest());
  }, [dispatch]);

  const cards = muhuratData?.data?.muhurts || [];

  const dateKey = (timestamp) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  };

  const cardsByStartDate = useMemo(() => {
    return cards.reduce((grouped, item) => {
      const key = dateKey(item.muhurtStart);
      if (!key) return grouped;
      grouped[key] = grouped[key] ? [...grouped[key], item] : [item];
      return grouped;
    }, {});
  }, [cards]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth);

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1),
    );
  };

  const formatDate = (timestamp) => {
    const d = new Date(timestamp);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getDuration = (start, end) => {
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
    return `${hours} hours ${minutes} minutes`;
  };

  const handleDateClick = (key) => {
    if (!cardsByStartDate[key]) return;
    setSelectedDate(key);
    const firstCard = cardsByStartDate[key][0];
    cardRefs.current[firstCard._id]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box
      sx={{
        color: themeColors.text,
        background: themeColors.bigContainer,
      }}
    >
      <Typography mb={2} sx={{ color: themeColors.text }}>
        Upcoming Days
      </Typography>

      {/*CARDS*/}
      <Box
        display="flex"
        gap={2}
        overflow="auto"
        mb={4}
        sx={{
          color: themeColors.text,
          background: themeColors.bigContainer,
        }}
      >
        {cards.map((item) => {
          const isSelected = dateKey(item.muhurtStart) === selectedDate;
          return (
          <Box
            key={item._id}
            ref={(node) => {
              cardRefs.current[item._id] = node;
            }}
            sx={{
              minWidth: 260,
              p: 2,
              borderRadius: 3,
              background: isSelected ? "#fff6d8" : "#d4c8c8",
              border: isSelected
                ? `3px solid ${themeColors.primary}`
                : "3px solid transparent",
              boxShadow: isSelected
                ? `0 0 0 2px ${themeColors.primary}`
                : "none",
            }}
          >
            <Typography>On {formatDate(item.muhurtStart)}</Typography>

            <Typography variant="h6">{item.title}</Typography>

            <Typography variant="body2">
              will remain for {getDuration(item.muhurtStart, item.muhurtEnd)}
            </Typography>

            {item.url && (
              <Box
                component="img"
                src={item.url}
                alt={item.title}
                sx={{
                  width: "100%",
                  height: 180,
                  objectFit: "cover",
                  borderRadius: 2,
                  mt: 1,
                }}
              />
            )}
          </Box>
          );
        })}
      </Box>

      {/* CALENDAR */}
      <Box
        sx={{
          background: themeColors.primary,
          borderRadius: 3,
          p: 4,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <IconButton onClick={handlePrevMonth} size="small">
            <ChevronLeft sx={{ color: "#fff" }} />
          </IconButton>

          <Typography variant="h6" fontWeight={600} color="#fff">
            {monthNames[currentMonth.getMonth()]}
          </Typography>

          <IconButton onClick={handleNextMonth} size="small">
            <ChevronRight sx={{ color: "#fff" }} />
          </IconButton>
        </Box>

        <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1} mb={1}>
          {daysOfWeek.map((day) => (
            <Box key={day} textAlign="center">
              <Typography variant="body2" color="#fff" fontWeight={500}>
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
          {Array.from({ length: firstDay }).map((_, i) => (
            <Box key={`empty-${i}`} />
          ))}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const key = dateKey(
              new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day),
            );
            const isMuhuratDay = Boolean(cardsByStartDate[key]);
            const isSelected = selectedDate === key;

            return (
              <Box
                key={day}
                onClick={() => handleDateClick(key)}
                sx={{
                  aspectRatio: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  color: isSelected ? themeColors.primary : "#fff",
                  cursor: isMuhuratDay ? "pointer" : "default",
                  fontWeight: isMuhuratDay ? 700 : 400,
                  background: isSelected
                    ? "#fff"
                    : isMuhuratDay
                      ? "rgba(255,255,255,0.35)"
                      : "transparent",
                  border: isMuhuratDay ? "2px solid #fff" : "2px solid transparent",
                  "&:hover": isMuhuratDay
                    ? { background: isSelected ? "#fff" : "rgba(255,255,255,0.5)" }
                    : {},
                }}
              >
                <Typography fontWeight={isMuhuratDay ? 700 : 400}>{day}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
